import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = 'https://zsioxpbyibyvmfrskkmd.supabase.co'
const SUPABASE_ANON_KEY =
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpzaW94cGJ5aWJ5dm1mcnNra21kIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzE2ODE0MjcsImV4cCI6MjA4NzI1NzQyN30.VYZ6SrtjdcgaX-4diZv_KabPSv_qpwIi21-w_3xjmMU'

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

// ─── TYPES ────────────────────────────────────────────────────────────────────

export interface SupabaseUser {
  id?: string
  email: string
  phone?: string
  name: string
  brand_name?: string
  password: string // hashed
  plan: string
  country?: string
  instance_name?: string
  system_message?: string
  trial_start_date?: string
  trial_end_date?: string
  is_trial_active?: boolean
  is_account_suspended?: boolean
  dolibarr_id?: string
  created_at?: string
  updated_at?: string
}

export interface SupabaseProduct {
  id?: string
  user_email: string
  name: string
  type: string
  price: string
  description?: string
  link?: string
  created_at?: string
}

export interface SupabaseOffer {
  id?: string
  user_email: string
  title: string
  discount: string
  start_date?: string
  end_date?: string
  link?: string
  active?: boolean
  created_at?: string
}

export interface SupabaseQA {
  id?: string
  user_email: string
  question: string
  answer: string
  details?: string
  created_at?: string
}

export interface SupabaseCustomer {
  id?: string
  instance_name: string
  name: string
  phone?: string
  note?: string
  condition?: string
  created_at?: string
}

// ─── SQL SCHEMA (run this in Supabase SQL Editor) ─────────────────────────────
/*
-- Run this SQL in your Supabase SQL Editor to create all tables:

CREATE TABLE IF NOT EXISTS public.users (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  email TEXT UNIQUE NOT NULL,
  phone TEXT,
  name TEXT NOT NULL,
  brand_name TEXT,
  password TEXT NOT NULL,
  plan TEXT DEFAULT 'trial',
  country TEXT,
  instance_name TEXT,
  system_message TEXT,
  trial_start_date TIMESTAMPTZ,
  trial_end_date TIMESTAMPTZ,
  is_trial_active BOOLEAN DEFAULT true,
  is_account_suspended BOOLEAN DEFAULT false,
  dolibarr_id TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW(),
  updated_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS public.products (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_email TEXT NOT NULL REFERENCES public.users(email) ON DELETE CASCADE,
  name TEXT NOT NULL,
  type TEXT DEFAULT 'product',
  price TEXT,
  description TEXT,
  link TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS public.offers (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_email TEXT NOT NULL REFERENCES public.users(email) ON DELETE CASCADE,
  title TEXT NOT NULL,
  discount TEXT,
  start_date TEXT,
  end_date TEXT,
  link TEXT,
  active BOOLEAN DEFAULT true,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS public.qa_items (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_email TEXT NOT NULL REFERENCES public.users(email) ON DELETE CASCADE,
  question TEXT NOT NULL,
  answer TEXT,
  details TEXT,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

CREATE TABLE IF NOT EXISTS public.customers (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  user_email TEXT NOT NULL REFERENCES public.users(email) ON DELETE CASCADE,
  name TEXT NOT NULL,
  phone TEXT,
  email TEXT,
  conversations INTEGER DEFAULT 0,
  created_at TIMESTAMPTZ DEFAULT NOW()
);

-- Enable Row Level Security (RLS) - optional but recommended
ALTER TABLE public.users ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.products ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.offers ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.qa_items ENABLE ROW LEVEL SECURITY;
ALTER TABLE public.customers ENABLE ROW LEVEL SECURITY;

-- Allow all operations via anon key (since we handle auth ourselves)
CREATE POLICY "Allow all" ON public.users FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow all" ON public.products FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow all" ON public.offers FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow all" ON public.qa_items FOR ALL USING (true) WITH CHECK (true);
CREATE POLICY "Allow all" ON public.customers FOR ALL USING (true) WITH CHECK (true);
*/

// ─── USER OPERATIONS ──────────────────────────────────────────────────────────

export const dbUsers = {
  // Create new user
  create: async (userData: SupabaseUser): Promise<SupabaseUser | null> => {
    try {
      const insertPayload = {
        email: userData.email.toLowerCase(),
        phone: userData.phone || '',
        name: userData.name,
        brand_name: userData.brand_name || '',
        password: userData.password,
        plan: userData.plan || 'trial',
        country: userData.country || '',
        instance_name: userData.instance_name || '',
        system_message: userData.system_message || '',
        trial_start_date: userData.trial_start_date || null,
        trial_end_date: userData.trial_end_date || null,
        is_trial_active: userData.is_trial_active ?? true,
        is_account_suspended: userData.is_account_suspended ?? false,
        dolibarr_id: userData.dolibarr_id || '',
      }

      console.log('🔵 Supabase INSERT payload:', {
        ...insertPayload,
        password: '[HASHED]',
      })

      const { data, error } = await supabase
        .from('users')
        .insert([insertPayload])
        .select()
        .single()

      if (error) {
        console.error('🔴 Supabase INSERT error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
        })
        return null
      }

      console.log('✅ Supabase INSERT success:', data?.email)
      return data
    } catch (err) {
      console.error('🔴 Supabase create user exception:', err)
      return null
    }
  },

  // Get user by email
  getByEmail: async (email: string): Promise<SupabaseUser | null> => {
    try {
      console.log('🔍 Supabase: Looking up user by email:', email.toLowerCase())

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('email', email.toLowerCase())
        .maybeSingle()

      if (error) {
        console.error(
          '🔴 Supabase getByEmail error:',
          error.message,
          error.code,
          error.details,
        )
        return null
      }

      if (!data) {
        console.log(
          '⚠️ Supabase: No user found for email:',
          email.toLowerCase(),
        )
        return null
      }

      console.log('✅ Supabase: User found:', data.email, '| Plan:', data.plan)
      return data
    } catch (err) {
      console.error('🔴 Supabase getByEmail exception:', err)
      return null
    }
  },

  // Update user fields
  update: async (
    email: string,
    updates: Partial<SupabaseUser>,
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('users')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('email', email.toLowerCase())

      if (error) {
        console.error('Supabase update user error:', error)
        return false
      }
      return true
    } catch (err) {
      console.error('Supabase update user exception:', err)
      return false
    }
  },

  // Check if email exists
  emailExists: async (email: string): Promise<boolean> => {
    try {
      const { data } = await supabase
        .from('users')
        .select('email')
        .eq('email', email.toLowerCase())
        .single()
      return !!data
    } catch {
      return false
    }
  },

  // Get all users (admin)
  getAll: async (): Promise<SupabaseUser[]> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) return []
      return data || []
    } catch {
      return []
    }
  },

  // Suspend expired trials
  suspendExpired: async (): Promise<void> => {
    try {
      const now = new Date().toISOString()
      await supabase
        .from('users')
        .update({ is_account_suspended: true, is_trial_active: false })
        .eq('plan', 'trial')
        .eq('is_account_suspended', false)
        .lt('trial_end_date', now)
    } catch (err) {
      console.error('Supabase suspendExpired error:', err)
    }
  },
}

// ─── PRODUCTS OPERATIONS ──────────────────────────────────────────────────────

export const dbProducts = {
  getAll: async (userEmail: string): Promise<SupabaseProduct[]> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('user_email', userEmail.toLowerCase())
        .order('created_at', { ascending: false })
      if (error) return []
      return data || []
    } catch {
      return []
    }
  },

  create: async (
    product: Omit<SupabaseProduct, 'id' | 'created_at'>,
  ): Promise<SupabaseProduct | null> => {
    try {
      const { data, error } = await supabase
        .from('products')
        .insert([{ ...product, user_email: product.user_email.toLowerCase() }])
        .select()
        .single()
      if (error) {
        console.error('Supabase create product error:', error)
        return null
      }
      return data
    } catch {
      return null
    }
  },

  update: async (
    id: string,
    updates: Partial<SupabaseProduct>,
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id)
      return !error
    } catch {
      return false
    }
  },

  delete: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('products').delete().eq('id', id)
      return !error
    } catch {
      return false
    }
  },
}

// ─── OFFERS OPERATIONS ────────────────────────────────────────────────────────

export const dbOffers = {
  getAll: async (userEmail: string): Promise<SupabaseOffer[]> => {
    try {
      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .eq('user_email', userEmail.toLowerCase())
        .order('created_at', { ascending: false })
      if (error) return []
      return data || []
    } catch {
      return []
    }
  },

  create: async (
    offer: Omit<SupabaseOffer, 'id' | 'created_at'>,
  ): Promise<SupabaseOffer | null> => {
    try {
      const { data, error } = await supabase
        .from('offers')
        .insert([{ ...offer, user_email: offer.user_email.toLowerCase() }])
        .select()
        .single()
      if (error) {
        console.error('Supabase create offer error:', error)
        return null
      }
      return data
    } catch {
      return null
    }
  },

  update: async (
    id: string,
    updates: Partial<SupabaseOffer>,
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('offers')
        .update(updates)
        .eq('id', id)
      return !error
    } catch {
      return false
    }
  },

  delete: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('offers').delete().eq('id', id)
      return !error
    } catch {
      return false
    }
  },
}

// ─── Q&A OPERATIONS ───────────────────────────────────────────────────────────

export const dbQA = {
  getAll: async (userEmail: string): Promise<SupabaseQA[]> => {
    try {
      const { data, error } = await supabase
        .from('qa_items')
        .select('*')
        .eq('user_email', userEmail.toLowerCase())
        .order('created_at', { ascending: false })
      if (error) return []
      return data || []
    } catch {
      return []
    }
  },

  create: async (
    qa: Omit<SupabaseQA, 'id' | 'created_at'>,
  ): Promise<SupabaseQA | null> => {
    try {
      const { data, error } = await supabase
        .from('qa_items')
        .insert([{ ...qa, user_email: qa.user_email.toLowerCase() }])
        .select()
        .single()
      if (error) {
        console.error('Supabase create QA error:', error)
        return null
      }
      return data
    } catch {
      return null
    }
  },

  update: async (
    id: string,
    updates: Partial<SupabaseQA>,
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('qa_items')
        .update(updates)
        .eq('id', id)
      return !error
    } catch {
      return false
    }
  },

  delete: async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('qa_items').delete().eq('id', id)
      return !error
    } catch {
      return false
    }
  },
}

// ─── CUSTOMERS OPERATIONS ─────────────────────────────────────────────────────

export const dbCustomers = {
  // Fetch all customers belonging to this instance
  getAll: async (
    _userEmail: string,
    instanceName?: string,
  ): Promise<SupabaseCustomer[]> => {
    try {
      if (!instanceName) return []
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('instance_name', instanceName)
        .order('created_at', { ascending: false })
      if (error) {
        console.error('Supabase getAll customers error:', error)
        return []
      }
      return data || []
    } catch {
      return []
    }
  },

  create: async (
    customer: Omit<SupabaseCustomer, 'id' | 'created_at'>,
  ): Promise<SupabaseCustomer | null> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .insert([customer])
        .select()
        .single()
      if (error) {
        console.error('Supabase create customer error:', error)
        return null
      }
      return data
    } catch {
      return null
    }
  },

  update: async (
    instanceName: string,
    phone: string,
    updates: Partial<
      Pick<SupabaseCustomer, 'name' | 'phone' | 'note' | 'condition'>
    >,
  ): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('customers')
        .update(updates)
        .eq('instance_name', instanceName)
        .eq('phone', phone)
      return !error
    } catch {
      return false
    }
  },

  delete: async (instanceName: string, phone: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('instance_name', instanceName)
        .eq('phone', phone)
      return !error
    } catch {
      return false
    }
  },
}

// ─── CLIENT BRANDS (for homepage marquee) ────────────────────────────────────

export const dbClientBrands = {
  // Get all active client brand names for the homepage marquee
  getAll: async (): Promise<{ brand_name: string; created_at: string }[]> => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('brand_name, created_at')
        .not('brand_name', 'is', null)
        .neq('brand_name', '')
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) return []
      return (data || []).filter(
        (u) => u.brand_name && u.brand_name.trim().length > 0,
      )
    } catch {
      return []
    }
  },
}
